import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { useLocation } from "wouter"
import { useQueryClient } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Link2, Loader2, Youtube, FileText, Globe } from "lucide-react"

interface RepurposingUrlModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RepurposingUrlModal({ open, onOpenChange }: RepurposingUrlModalProps) {
  const { toast } = useToast()
  const [, setLocation] = useLocation()
  const queryClient = useQueryClient()
  const [url, setUrl] = useState("")
  const [sourceType, setSourceType] = useState<"youtube" | "article" | "blog">("youtube")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateUrl = (urlString: string): boolean => {
    try {
      const urlObj = new URL(urlString)
      
      // YouTube validation
      if (sourceType === "youtube") {
        const isYoutube = 
          urlObj.hostname.includes('youtube.com') || 
          urlObj.hostname.includes('youtu.be')
        
        if (!isYoutube) {
          toast({
            title: "Ungültige YouTube-URL",
            description: "Bitte geben Sie einen gültigen YouTube-Link ein.",
            variant: "destructive"
          })
          return false
        }
      }
      
      // Article/Blog validation - just check if it's a valid URL
      if (sourceType === "article" || sourceType === "blog") {
        if (!urlObj.protocol.startsWith('http')) {
          toast({
            title: "Ungültige URL",
            description: "Bitte geben Sie eine gültige Adresse (http oder https) ein.",
            variant: "destructive"
          })
          return false
        }
      }
      
      return true
    } catch (error) {
      toast({
        title: "Ungültige URL",
        description: "Bitte geben Sie eine gültige Adresse ein.",
        variant: "destructive"
      })
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!url.trim()) {
      toast({
        title: "URL fehlt",
        description: "Bitte geben Sie eine Adresse ein.",
        variant: "destructive"
      })
      return
    }

    if (!validateUrl(url.trim())) {
      return
    }

    setIsSubmitting(true)

    try {
      // apiRequest (not bare fetch): errors carry .status/.data for the global access gate,
      // and the created row must be visible immediately — invalidate the list so the page
      // refetches instead of waiting for a poll that only runs when a pending item exists.
      await apiRequest('POST', '/api/repurposing-content', {
        url: url.trim(),
        sourceType,
      })

      queryClient.invalidateQueries({ queryKey: ['/api/repurposing-content'] })
      toast({
        title: "URL erfolgreich übermittelt!",
        description: "Die KI analysiert den Inhalt. Sie werden benachrichtigt, sobald die Beiträge fertig sind."
      })

      // Reset form
      setUrl("")
      setSourceType("youtube")

      // Close modal and land on the list (no-op if already there)
      setTimeout(() => {
        onOpenChange(false)
        setLocation('/repurposing')
      }, 1000)
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error?.data?.error || error?.message || "Die URL konnte nicht übermittelt werden.",
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const getSourceTypeIcon = (type: string) => {
    switch (type) {
      case 'youtube':
        return <Youtube className="h-4 w-4" />
      case 'article':
        return <FileText className="h-4 w-4" />
      case 'blog':
        return <Globe className="h-4 w-4" />
      default:
        return <Link2 className="h-4 w-4" />
    }
  }

  const getPlaceholder = () => {
    switch (sourceType) {
      case 'youtube':
        return 'https://www.youtube.com/watch?v=...'
      case 'article':
        return 'https://example.com/article/...'
      case 'blog':
        return 'https://blog.example.com/post/...'
      default:
        return 'https://...'
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Link2 className="h-6 w-6" />
            <span>Content Repurposing</span>
          </DialogTitle>
          <DialogDescription className="text-base">
            Geben Sie einen YouTube- oder Artikel-Link ein. Emma und Max erstellen daraus Beiträge für Sie.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {/* Source Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="sourceType" className="text-sm font-medium">
              Content-Typ
            </Label>
            <Select 
              value={sourceType} 
              onValueChange={(value: "youtube" | "article" | "blog") => setSourceType(value)}
              disabled={isSubmitting}
            >
              <SelectTrigger 
                id="sourceType" 
                className="w-full"
                data-testid="select-source-type"
              >
                <SelectValue placeholder="Content-Typ wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="youtube" data-testid="option-youtube">
                  <div className="flex items-center gap-2">
                    <Youtube className="h-4 w-4" />
                    <span>YouTube-Video</span>
                  </div>
                </SelectItem>
                <SelectItem value="article" data-testid="option-article">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>Artikel</span>
                  </div>
                </SelectItem>
                <SelectItem value="blog" data-testid="option-blog">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <span>Blog-Post</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="url" className="text-sm font-medium">
              URL
            </Label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                {getSourceTypeIcon(sourceType)}
              </div>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={getPlaceholder()}
                disabled={isSubmitting}
                className="pl-10"
                data-testid="input-repurposing-url"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {sourceType === 'youtube' && 'Fügen Sie einen YouTube-Link ein (youtube.com oder youtu.be)'}
              {sourceType === 'article' && 'Fügen Sie einen Artikel-Link ein'}
              {sourceType === 'blog' && 'Fügen Sie einen Blog-Link ein'}
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              data-testid="button-cancel-repurposing"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !url.trim()}
              data-testid="button-submit-repurposing"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird übermittelt...
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 mr-2" />
                  URL hinzufügen
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
